import React, { useState } from 'react';
import { Box, Paper, Typography, Avatar, Divider, Link, Button, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Alert, CircularProgress } from '@mui/material';
import AppStyles from '../styles/AppStyles';
import ErrorState from '../components/ErrorState';
import { CardSkeleton } from '../components/SkeletonLoader';
import { LocationOn as LocationIcon, Email as EmailIcon, MedicalInformation as MedicalInformationIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useTranslatedLocation } from '../hooks/useTranslatedLocation';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

// Component to display translated location
const TranslatedLocationDisplay = ({ location }) => {
  const { data: locationString, isLoading } = useTranslatedLocation(location);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
      <LocationIcon sx={{ fontSize: 18, color: AppStyles.colors.primary.main }} />
      <Typography variant="body2" sx={{ color: AppStyles.colors.text.primary, fontWeight: AppStyles.typography.fontWeight.medium }}>
        {isLoading ? '...' : (locationString || '-')}
      </Typography>
    </Box>
  );
};

export default function VetProfile() {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3000';
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [vetToDelete, setVetToDelete] = useState(null);

  const vetQuery = useQuery({
    queryKey: ['vetProfile'],
    queryFn: async () => {
      const token = localStorage.getItem('vetToken');
      const resp = await fetch(`${API_BASE}/api/sharing/me/vet`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data?.message || t('vetProfile.failedToLoadProfile'));
      return Array.isArray(data?.data) ? data.data : [];
    }
  });

  const loading = vetQuery.isLoading;
  const error = vetQuery.isError;
  const vetProfile = vetQuery.data || [];

  const handleDeleteClick = (vet) => {
    setVetToDelete(vet);
    setDeleteDialogOpen(true);
    setDeleteError(null);
  };

  const handleDeleteConfirm = async () => {
    if (!vetToDelete) return;
    
    setDeleting(true);
    setDeleteError(null);
    
    try {
      const token = localStorage.getItem('vetToken');
      const resp = await fetch(`${API_BASE}/api/sharing/me/vet`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = await resp.json();
      
      if (!resp.ok) {
        throw new Error(data?.message || data?.error || t('vetProfile.deleteFailed'));
      }
      
      // Success - set flag in sessionStorage, logout, then redirect to login
      sessionStorage.setItem('accountDeleted', 'true');
      logout();
      navigate('/login', { replace: true });
    } catch (err) {
      console.error('Delete account error:', err);
      setDeleteError(err.message || t('vetProfile.deleteFailed'));
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setVetToDelete(null);
    setDeleteError(null);
  };

  return (
    <Box sx={{ p: 2 }}>
      {loading && <CardSkeleton count={2} />}
      {error && <ErrorState />}

      {!loading && !error && (
        <>
          {vetProfile.map((v) => (
            <Paper
              key={v._id}
              sx={{
                px: 2.5,
                py: 2,
                mb: 3,
                borderRadius: 3,
                background: 'transparent',
                border: `1px solid ${AppStyles.colors.border.light}`,
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '4px',
                  background: `linear-gradient(90deg, ${AppStyles.colors.primary.main} 0%, ${AppStyles.colors.secondary.main} 100%)`,
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                <Avatar
                  sx={{
                    width: 50,
                    height: 50,
                    color: AppStyles.colors.primary.contrastText,
                    background: `linear-gradient(135deg, ${AppStyles.colors.primary.main} 0%, ${AppStyles.colors.secondary.main} 100%)`,
                    fontWeight: AppStyles.typography.fontWeight.bold,
                  }}
                >
                  {(v.vetName || 'V').split(' ').filter(Boolean).map(s => s[0]).join('').slice(0,2).toUpperCase()}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h5" sx={{ color: AppStyles.colors.primary.dark, fontWeight: AppStyles.typography.fontWeight.semibold, mb: 0.25 }}>
                    {v.vetType === 'individual' ? `${t('vetProfile.drPrefix')}${v.vetName}` : (v.vetName || '-')}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25 }}>
                    <MedicalInformationIcon sx={{ fontSize: 18, color: AppStyles.colors.primary.main }} />
                    <Typography variant="body2" sx={{ color: AppStyles.colors.text.primary, fontWeight: AppStyles.typography.fontWeight.medium, textTransform: 'capitalize' }}>
                      {v.vetType || '-'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25 }}>
                    <EmailIcon sx={{ fontSize: 18, color: AppStyles.colors.primary.main }} />
                    {v.vetEmail ? (
                      <Link href={`mailto:${v.vetEmail}`} underline="hover" sx={{ color: AppStyles.colors.text.primary, fontWeight: AppStyles.typography.fontWeight.medium }}>
                        {v.vetEmail}
                      </Link>
                    ) : (
                      <Typography variant="body2" sx={{ color: AppStyles.colors.text.primary, fontWeight: AppStyles.typography.fontWeight.medium }}>-</Typography>
                    )}
                  </Box>
                </Box>
              </Box>

              <Divider sx={{ my: 1.5, borderColor: AppStyles.colors.border.light, opacity: 0.6 }} />

              {(v.countryCode || v.regionCode || v.cityId) && (
                <TranslatedLocationDisplay 
                  location={{
                    countryCode: v.countryCode,
                    regionCode: v.regionCode,
                    cityId: v.cityId,
                  }}
                />
              )}

              {v.website && (
                <Box>
                  <Typography variant="overline" sx={{ color: AppStyles.colors.text.secondary }}>{t('vetProfile.website')}</Typography>
                  <Typography variant="body1" sx={{ color: AppStyles.colors.text.primary, fontWeight: AppStyles.typography.fontWeight.medium }}>
                    {v.website}
                  </Typography>
                </Box>
              )}

              <Divider sx={{ my: 2, borderColor: AppStyles.colors.border.light, opacity: 0.6 }} />

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => handleDeleteClick(v)}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: AppStyles.typography.fontWeight.medium,
                    borderColor: AppStyles.colors.error.main,
                    color: AppStyles.colors.error.main,
                    '&:hover': {
                      borderColor: AppStyles.colors.error.dark,
                      backgroundColor: `${AppStyles.colors.error.main}10`,
                    }
                  }}
                >
                  {t('vetProfile.deleteAccount')}
                </Button>
              </Box>
            </Paper>
          ))}

          {vetProfile.length === 0 && (
            <Typography variant="body1" sx={{ color: AppStyles.colors.text.secondary }}>{t('vetProfile.noProfileFound')}</Typography>
          )}
        </>
      )}

      {/* Delete Account Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: AppStyles.colors.error.main, fontWeight: AppStyles.typography.fontWeight.bold }}>
          {t('vetProfile.deleteAccount')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t('vetProfile.deleteAccountWarning')}
          </DialogContentText>
          {deleteError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {deleteError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleDeleteCancel}
            disabled={deleting}
            sx={{ textTransform: 'none' }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
            sx={{
              textTransform: 'none',
              fontWeight: AppStyles.typography.fontWeight.medium,
            }}
          >
            {deleting ? t('vetProfile.deleting') : t('vetProfile.deleteConfirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}


